import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatCurrency, formatPercent, formatDate, formatNumber, cn } from '@/lib/utils'
import { MetricCard, LoadingSpinner } from '@/components/ui'
import {
  ResponsiveContainer, BarChart as RechartsBarChart, Bar, LineChart, Line,
  AreaChart, Area, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, Cell,
} from 'recharts'
import { TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart, ArrowLeft } from 'lucide-react'

export default function AUMMonitoring() {
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null)
  const [historyMonths, setHistoryMonths] = useState(60)

  // Load AUM overview
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['aum-overview'],
    queryFn: () => api.get('/aum/overview').then((r) => r.data),
  })

  // Load latest capital flows
  const { data: latestFlows } = useQuery({
    queryKey: ['aum-flows-latest'],
    queryFn: () => api.get('/aum/flows/latest').then((r) => r.data),
  })

  // Load full flows time series (12 months)
  const { data: _allFlows } = useQuery({
    queryKey: ['aum-flows-all'],
    queryFn: () => api.get('/aum/flows?months=12').then((r) => r.data),
  })

  // Load portfolio weights
  const { data: portfolio } = useQuery({
    queryKey: ['aum-portfolio'],
    queryFn: () => api.get('/aum/portfolio').then((r) => r.data),
  })

  // Load fund detail when a fund is selected
  const { data: fundDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['aum-fund-detail', selectedFundId, historyMonths],
    queryFn: () => api.get(`/aum/fund/${selectedFundId}/detail?months=${historyMonths}`).then((r) => r.data),
    enabled: !!selectedFundId,
  })

  // Load OPERA data for selected fund
  const { data: operaData } = useQuery({
    queryKey: ['aum-opera', selectedFundId],
    queryFn: () => api.get(`/aum/opera/${selectedFundId}`).then((r) => r.data),
    enabled: !!selectedFundId,
  })

  // Summary stats
  const totalAUM = overview?.reduce((s: number, f: any) => s + (f.fund_aum || 0), 0) || 0
  const fundCount = overview?.length || 0
  const totalNetFlow = latestFlows?.reduce((s: number, f: any) => s + (f.capital_flow || 0), 0) || 0
  const totalPortfolio = portfolio?.reduce((s: number, p: any) => s + (p.allocation_amount || 0), 0) || 0

  // Aggregation chart data: growth rates by fund
  const growthRateData = useMemo(() => {
    if (!latestFlows) return []
    return latestFlows.map((f: any) => ({
      name: f.fund_name?.length > 20 ? f.fund_name.slice(0, 20) + '...' : f.fund_name,
      growth: f.prev_aum && f.fund_aum ? ((f.fund_aum - f.prev_aum) / f.prev_aum) * 100 : 0,
    })).sort((a: any, b: any) => b.growth - a.growth)
  }, [latestFlows])

  // Flow as % of AUM
  const flowPctData = useMemo(() => {
    if (!latestFlows) return []
    return latestFlows.map((f: any) => ({
      name: f.fund_name?.length > 20 ? f.fund_name.slice(0, 20) + '...' : f.fund_name,
      pct: f.fund_aum && f.capital_flow ? (f.capital_flow / f.fund_aum) * 100 : 0,
    })).sort((a: any, b: any) => b.pct - a.pct)
  }, [latestFlows])

  // Capital flows bar data
  const flowsBarData = useMemo(() => {
    if (!latestFlows) return []
    return latestFlows.map((f: any) => ({
      name: f.fund_name?.length > 20 ? f.fund_name.slice(0, 20) + '...' : f.fund_name,
      value: f.capital_flow || 0,
    })).sort((a: any, b: any) => b.value - a.value)
  }, [latestFlows])

  // Fund detail charts
  const aumFlowChart = useMemo(() => {
    if (!fundDetail?.flows) return []
    return fundDetail.flows.map((f: any) => ({
      date: f.date?.substring(0, 7),
      aum: f.fund_aum,
      pnl: f.pnl,
      flow: f.capital_flow,
    }))
  }, [fundDetail])

  const expectedFlowChart = useMemo(() => {
    if (!fundDetail?.expected_flows) return []
    return fundDetail.expected_flows.map((f: any) => ({
      date: f.date?.substring(0, 7),
      actual: f.actual,
      expected: f.expected,
    }))
  }, [fundDetail])

  // AUM vs performance scatter
  const aumPerfScatter = useMemo(() => {
    if (!fundDetail?.flows) return []
    return fundDetail.flows
      .filter((f: any) => f.performance != null && f.fund_aum != null)
      .map((f: any) => ({
        performance: (f.performance || 0) * 100,
        aum: f.fund_aum,
        date: f.date,
      }))
  }, [fundDetail])

  // OPERA chart data
  const operaChart = useMemo(() => {
    if (!operaData) return []
    const firmMap = new Map((operaData.firm_aum || []).map((d: any) => [d.date, d.value]))
    const stratMap = new Map((operaData.strategy_aum || []).map((d: any) => [d.date, d.value]))
    const allDates = new Set([...firmMap.keys(), ...stratMap.keys()])
    return [...allDates].sort().map(d => ({
      date: String(d)?.substring(0, 7),
      firm_aum: firmMap.get(d) ?? null,
      strategy_aum: stratMap.get(d) ?? null,
    }))
  }, [operaData])

  const selectedFund = overview?.find((f: any) => f.fund_id === selectedFundId)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">AUM Monitoring</h1>
        <p className="text-muted-foreground">Hedge Fund AUM Flow Analysis Dashboard</p>
      </div>

      {/* ─── Summary Cards ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Total Portfolio AUM" value={formatCurrency(totalAUM)} isLoading={overviewLoading}
          icon={<DollarSign className="h-4 w-4" />} />
        <MetricCard label="Number of Funds" value={fundCount} isLoading={overviewLoading}
          icon={<BarChart3 className="h-4 w-4" />} />
        <MetricCard label="Net Flows (Latest)" value={formatCurrency(totalNetFlow)} isLoading={!latestFlows}
          icon={totalNetFlow >= 0 ? <TrendingUp className="h-4 w-4 text-green-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />} />
        <MetricCard label="Portfolio Value" value={formatCurrency(totalPortfolio)} isLoading={!portfolio}
          icon={<PieChart className="h-4 w-4" />} />
      </div>

      {/* ─── Aggregation: 3 charts side by side ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Capital Flows by Fund */}
        {flowsBarData.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3">Capital Flows by Fund ($)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={flowsBarData} layout="vertical" margin={{ left: 5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                <Tooltip formatter={(v: number) => [formatCurrency(v), 'Flow']} />
                <ReferenceLine x={0} stroke="#666" />
                <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                  {flowsBarData.map((d: any, i: number) => (
                    <Cell key={i} fill={d.value >= 0 ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Growth Rates by Fund */}
        {growthRateData.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3">AUM Growth Rates (%)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={growthRateData} layout="vertical" margin={{ left: 5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${v.toFixed(1)}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                <Tooltip formatter={(v: number) => [`${v.toFixed(2)}%`, 'Growth']} />
                <ReferenceLine x={0} stroke="#666" />
                <Bar dataKey="growth" radius={[0, 2, 2, 0]}>
                  {growthRateData.map((d: any, i: number) => (
                    <Cell key={i} fill={d.growth >= 0 ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Flow as % of AUM */}
        {flowPctData.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3">Capital Flow as % of AUM</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={flowPctData} layout="vertical" margin={{ left: 5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${v.toFixed(1)}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                <Tooltip formatter={(v: number) => [`${v.toFixed(2)}%`, 'Flow %']} />
                <ReferenceLine x={0} stroke="#666" />
                <Bar dataKey="pct" radius={[0, 2, 2, 0]}>
                  {flowPctData.map((d: any, i: number) => (
                    <Cell key={i} fill={d.pct >= 0 ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ─── Portfolio Allocation ─── */}
      {portfolio && portfolio.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3">Portfolio Allocation</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 font-medium">Fund</th>
                  <th className="text-right py-2 font-medium">Allocation</th>
                  <th className="text-right py-2 font-medium">Weight</th>
                  <th className="py-2" style={{ width: '40%' }} />
                </tr>
              </thead>
              <tbody>
                {portfolio.map((p: any) => (
                  <tr key={p.fund_id} className="border-b border-border/50 hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedFundId(p.fund_id)}>
                    <td className="py-2 font-medium">{p.fund_name}</td>
                    <td className="py-2 text-right font-mono">{formatCurrency(p.allocation_amount)}</td>
                    <td className="py-2 text-right font-mono">{p.allocation_pct != null ? `${formatNumber(p.allocation_pct * 100, 1)}%` : '-'}</td>
                    <td className="py-2 px-2">
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-primary rounded-full h-2 transition-all"
                          style={{ width: `${Math.min((p.allocation_pct || 0) * 100, 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── AUM Overview Table ─── */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-semibold">AUM Overview</h2>
        </div>
        {overviewLoading ? (
          <LoadingSpinner />
        ) : !overview?.length ? (
          <div className="p-8 text-center text-muted-foreground">No AUM data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-2 px-4 font-medium">Fund</th>
                  <th className="text-right py-2 px-4 font-medium">Fund AUM</th>
                  <th className="text-right py-2 px-4 font-medium">Firm AUM</th>
                  <th className="py-2 px-4 font-medium">As Of</th>
                  <th className="py-2 px-4" />
                </tr>
              </thead>
              <tbody>
                {overview.map((f: any) => (
                  <tr key={f.fund_id}
                    className={cn(
                      'border-b border-border/50 cursor-pointer transition-colors',
                      selectedFundId === f.fund_id ? 'bg-primary/5' : 'hover:bg-muted/50'
                    )}
                    onClick={() => setSelectedFundId(f.fund_id)}>
                    <td className="py-2 px-4 font-medium">{f.fund_name}</td>
                    <td className="py-2 px-4 text-right font-mono">{formatCurrency(f.fund_aum)}</td>
                    <td className="py-2 px-4 text-right font-mono text-muted-foreground">{formatCurrency(f.firm_aum)}</td>
                    <td className="py-2 px-4 text-muted-foreground">{formatDate(f.date)}</td>
                    <td className="py-2 px-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedFundId(f.fund_id) }}
                        className="text-xs px-2 py-1 border border-input rounded hover:bg-accent">
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Capital Flows Detail Table (Latest Month) ─── */}
      {latestFlows && latestFlows.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold">Capital Flow Details (Latest Month)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-2 px-4 font-medium">Fund</th>
                  <th className="text-right py-2 px-4 font-medium">AUM</th>
                  <th className="text-right py-2 px-4 font-medium">Prev AUM</th>
                  <th className="text-right py-2 px-4 font-medium">Performance</th>
                  <th className="text-right py-2 px-4 font-medium">Capital Flow</th>
                </tr>
              </thead>
              <tbody>
                {latestFlows.map((f: any) => (
                  <tr key={f.fund_id} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="py-2 px-4 font-medium">{f.fund_name}</td>
                    <td className="py-2 px-4 text-right font-mono">{formatCurrency(f.fund_aum)}</td>
                    <td className="py-2 px-4 text-right font-mono text-muted-foreground">{formatCurrency(f.prev_aum)}</td>
                    <td className={cn('py-2 px-4 text-right font-mono', f.performance >= 0 ? 'text-green-600' : 'text-red-600')}>
                      {formatPercent(f.performance)}
                    </td>
                    <td className={cn('py-2 px-4 text-right font-mono font-bold', f.capital_flow >= 0 ? 'text-green-600' : 'text-red-600')}>
                      {formatCurrency(f.capital_flow)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* ─── Individual Fund Analysis ─── */}
      {/* ═══════════════════════════════════════════════════ */}
      {selectedFundId && (
        <div className="space-y-4 border-t-2 border-primary pt-6">
          <div className="flex items-center justify-between">
            <div>
              <button onClick={() => setSelectedFundId(null)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-1">
                <ArrowLeft className="h-3 w-3" /> Back to overview
              </button>
              <h2 className="text-xl font-bold">{selectedFund?.fund_name || `Fund ${selectedFundId}`}</h2>
            </div>
            <select
              className="h-9 px-3 border border-input rounded-md bg-background text-sm"
              value={historyMonths}
              onChange={(e) => setHistoryMonths(parseInt(e.target.value))}
            >
              <option value={12}>1 Year</option>
              <option value={24}>2 Years</option>
              <option value={60}>5 Years</option>
              <option value={120}>10 Years</option>
            </select>
          </div>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
          ) : fundDetail ? (
            <>
              {/* ─── Fund KPI Cards ─── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard label="Current AUM" value={formatCurrency(fundDetail.kpis?.current_aum)}
                  icon={<DollarSign className="h-4 w-4" />} />
                <MetricCard label="Latest Performance"
                  value={formatPercent(fundDetail.kpis?.latest_performance)}
                  icon={fundDetail.kpis?.latest_performance >= 0
                    ? <TrendingUp className="h-4 w-4 text-green-600" />
                    : <TrendingDown className="h-4 w-4 text-red-600" />} />
                <MetricCard label="3-Month Avg Flow" value={formatCurrency(fundDetail.kpis?.avg_3m_flow)}
                  subtitle="Monthly average" />
                <MetricCard label="12-Month Total Flow" value={formatCurrency(fundDetail.kpis?.total_12m_flow)}
                  subtitle="Cumulative" />
              </div>

              {/* ─── AUM History + Flows Dual Chart ─── */}
              {aumFlowChart.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* AUM bar chart colored by flow trend */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3">Total AUM (colored by flow trend)</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <RechartsBarChart data={aumFlowChart}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={2} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v)} />
                        <Tooltip formatter={(v: number, name: string) => [formatCurrency(v), name === 'aum' ? 'AUM' : name]} />
                        <Bar dataKey="aum" name="AUM" radius={[2, 2, 0, 0]}>
                          {aumFlowChart.map((d: any, i: number) => (
                            <Cell key={i} fill={
                              d.flow > 0 ? '#22c55e' : d.flow < 0 ? '#ef4444' : '#3b82f6'
                            } />
                          ))}
                        </Bar>
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Monthly change decomposition: P&L vs Capital Flow */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3">Monthly Change: P&L vs Capital Flow</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <RechartsBarChart data={aumFlowChart}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={2} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v)} />
                        <Tooltip formatter={(v: number, name: string) => [formatCurrency(v), name]} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <ReferenceLine y={0} stroke="#666" />
                        <Bar dataKey="pnl" name="P&L" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="flow" name="Net Capital Flow" fill="#93c5fd" radius={[2, 2, 0, 0]} />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ─── Expected Flows Chart ─── */}
              {expectedFlowChart.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-3">Actual vs Expected Flows (EWMA)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsBarChart data={expectedFlowChart}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={2} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v)} />
                      <Tooltip formatter={(v: number, name: string) => [formatCurrency(v), name]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <ReferenceLine y={0} stroke="#666" />
                      <Bar dataKey="actual" name="Actual Flow" fill="#93c5fd" radius={[2, 2, 0, 0]} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                  {/* Overlay expected line via a separate line chart */}
                  <div style={{ marginTop: -250, pointerEvents: 'none' }}>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={expectedFlowChart} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <XAxis dataKey="date" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Line type="monotone" dataKey="expected" name="Expected (EWMA)" stroke="#ef4444"
                          strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#ef4444' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ─── Fund AUM Trend + AUM vs Performance (2-col) ─── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Fund AUM Trend */}
                {fundDetail.history?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3">Fund AUM Trend</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={fundDetail.history.map((h: any) => ({
                        date: h.date?.substring(0, 7),
                        fund_aum: h.fund_aum,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v)} />
                        <Tooltip formatter={(v: number) => [formatCurrency(v), 'AUM']} />
                        <Area type="monotone" dataKey="fund_aum" stroke="#3b82f6" fill="#3b82f6"
                          fillOpacity={0.15} strokeWidth={2} dot={{ r: 2 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* AUM vs Performance Correlation */}
                {aumPerfScatter.length > 3 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3">AUM vs Performance</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="performance" name="Performance" type="number" tick={{ fontSize: 10 }}
                          label={{ value: 'Monthly Return (%)', position: 'insideBottom', offset: -5, fontSize: 10 }} />
                        <YAxis dataKey="aum" name="AUM" type="number" tick={{ fontSize: 10 }}
                          tickFormatter={v => formatCurrency(v)}
                          label={{ value: 'AUM', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                        <Tooltip formatter={(v: number, name: string) =>
                          [name === 'AUM' ? formatCurrency(v) : `${v.toFixed(2)}%`, name]} />
                        <Scatter data={aumPerfScatter} fill="#3b82f6" fillOpacity={0.6}>
                          {aumPerfScatter.map((_: any, i: number) => {
                            const perf = aumPerfScatter[i].performance
                            const fill = perf > 2 ? '#22c55e' : perf < -2 ? '#ef4444' : '#f59e0b'
                            return <Cell key={i} fill={fill} />
                          })}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* ─── OPERA Firm vs Strategy AUM ─── */}
              {operaChart.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-3">OPERA: Firm AUM (1.2.2) vs Strategy AUM (1.3.1)</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={operaChart}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v)} />
                      <Tooltip formatter={(v: number, name: string) => [formatCurrency(v), name]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="firm_aum" name="Firm AUM (1.2.2)" stroke="#3b82f6"
                        strokeWidth={2} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="strategy_aum" name="Strategy AUM (1.3.1)" stroke="#f59e0b"
                        strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ─── Raw Flow Data Table ─── */}
              {fundDetail.flows?.length > 0 && (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <details>
                    <summary className="p-4 cursor-pointer hover:bg-muted/30 font-semibold text-sm">
                      Detailed Flow Data ({fundDetail.flows.length} months)
                    </summary>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border bg-muted/30">
                            <th className="py-1.5 px-2 text-left font-medium">Date</th>
                            <th className="py-1.5 px-2 text-right font-medium">Fund AUM</th>
                            <th className="py-1.5 px-2 text-right font-medium">Firm AUM</th>
                            <th className="py-1.5 px-2 text-right font-medium">Perf %</th>
                            <th className="py-1.5 px-2 text-right font-medium">P&L</th>
                            <th className="py-1.5 px-2 text-right font-medium">Flow</th>
                            <th className="py-1.5 px-2 text-right font-medium">Growth %</th>
                            <th className="py-1.5 px-2 text-right font-medium">Flow/AUM %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...fundDetail.flows].reverse().map((f: any, i: number) => (
                            <tr key={i} className="border-b border-border/30 hover:bg-muted/50">
                              <td className="py-1 px-2 font-mono">{f.date}</td>
                              <td className="py-1 px-2 text-right font-mono">{formatCurrency(f.fund_aum)}</td>
                              <td className="py-1 px-2 text-right font-mono text-muted-foreground">{formatCurrency(f.firm_aum)}</td>
                              <td className={cn('py-1 px-2 text-right font-mono', f.performance >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {formatPercent(f.performance)}
                              </td>
                              <td className="py-1 px-2 text-right font-mono">{formatCurrency(f.pnl)}</td>
                              <td className={cn('py-1 px-2 text-right font-mono', f.capital_flow >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {formatCurrency(f.capital_flow)}
                              </td>
                              <td className={cn('py-1 px-2 text-right font-mono', f.growth_rate >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {f.growth_rate != null ? `${(f.growth_rate * 100).toFixed(2)}%` : '-'}
                              </td>
                              <td className={cn('py-1 px-2 text-right font-mono', (f.flow_pct_aum || 0) >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {f.flow_pct_aum != null ? `${(f.flow_pct_aum * 100).toFixed(2)}%` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
